import BridgeBaseResponse from '@/api/android/interfaces/BridgeBaseResponse';
import axios from '@/api/server/config';
import UserError from '@/error';
import OrderCloudCustomer from '@/interfaces/OrderCloudCustomer';
import RequestAdLog from '@/interfaces/RequestAdLog';
import RequestAuthLicense from '@/interfaces/RequestAuthLicense';
import RequestAuthMacAddress from '@/interfaces/RequestAuthMacAddress';
import RequestLogin from '@/interfaces/RequestLogin';
import RequestPoint from '@/interfaces/RequestPoint';
import ResponseAd from '@/interfaces/ResponseAd';
import ResponseAuthLicense from '@/interfaces/ResponseAuthLicense';
import ResponseConfigOption from '@/interfaces/ResponseConfigOptions';
import ResponseMenuData from '@/interfaces/ResponseMenuData';
import ResponsePoint from '@/interfaces/ResponsePoint';
import ResponseServerConfig from '@/interfaces/ResponseServerConfig';
import { logger } from '@/logger';
import { useConfigStore } from '@/stores/config';
import { AxiosError } from 'axios';
import { LogLevel } from 'vue-logger-plugin';

/**
 * 대리점 로그인 성공하면 회사 코드 리턴
 * HTTP Code 401 - 로그인 실패
 * @param requestLogin 로그인 데이터
 * @returns 회사 코드
 */
export const login = async (requestLogin: RequestLogin): Promise<number> => {
  try {
    logger.info('[Server] Login Request', requestLogin);
    const response = await axios.post<number>('/auth/login', requestLogin);
    logger.info('[Server] Login Response', response.data);
    return response.data;
  } catch (error: any) {
    if (error instanceof AxiosError && error.response && error.response.status === 401) {
      throw new UserError('로그인 실패');
    } else {
      throw error;
    }
  }
};

/**
 * Mac Address 인증
 * true - 인증 성공, false - 다른 매장에서 사용중인 MAC 주소
 * @param requestAuthMacAddress POS 인증 요청 데이터
 * @returns 인증 성공 실패 여부
 */
export const authMacAddress = async (requestAuthMacAddress: RequestAuthMacAddress): Promise<boolean> => {
  logger.info('[Server] AuthMacAddress Request', requestAuthMacAddress);
  const response = await axios.post<boolean>('/auth/authMacAddress', requestAuthMacAddress);
  logger.info('[Server] AuthMacAddress Response', response.data);
  return response.data;
};

/**
 * 제품키 인증
 * @param requestAuthLicense 제품키 인증 요청 데이터
 * @returns 제품키 인증 결과
 */
export const authLicense = async (requestAuthLicense: RequestAuthLicense): Promise<ResponseAuthLicense> => {
  logger.info('[Server] AuthLicense Request', requestAuthLicense);
  const response = await axios.post<ResponseAuthLicense>('/auth/authLicense', requestAuthLicense);
  logger.info('[Server] AuthLicense Response', response.data);
  return response.data;
};

/**
 * 서버 설정 조회
 * @returns 설정 데이터
 */
export const getConfig = async (): Promise<ResponseServerConfig> => {
  logger.info('[Server] GetConfig Request');
  const localConfig = useConfigStore().localConfig;
  const response = await axios.get('/config', {
    params: {
      tableCode: localConfig.TableCode,
    },
  });
  //logger.info('[Server] GetConfig Response', response);

  if (response.status === 204) throw new UserError('ASP에서 UP오더 설정을 해주세요.');

  // 현금 결제 비활성화 시간대 설정
  response.data.CashPaymentOffTimes = JSON.parse(response.data.CashPaymentOffTimes);
  return response.data as ResponseServerConfig;
};

/** 설정에서 사용되는 옵션 리스트 조회 */
export const getConfigOptions = async (macAddress: string): Promise<ResponseConfigOption> => {
  logger.info('[Server] GetConfigOptions Request', macAddress);
  const response = await axios.get<ResponseConfigOption>('/config/options', {
    params: { MacAddress: macAddress },
  });
  logger.info('[Server] GetConfigOptions Response', response.data);
  return response.data;
};

/**
 * 메뉴 데이터 조회 및 표시 순으로 정렬
 * @returns 매장 데이터
 */
export const getMenuData = async (menupanCode?: string): Promise<ResponseMenuData> => {
  logger.info('[Server] GetMenuData Request', menupanCode);
  const response = await axios.get<ResponseMenuData>('/menu-data2', {
    params: {
      menupanCode,
    },
  });
  //logger.info('[Server] GetMenuData Response', response);
  return response.data;
};

/**
 * 품절된 아이템 코드 리스트 조회
 * @returns 품절 아이템 코드 리스트
 */
export const getSoldoutData = async (): Promise<string[]> => {
  logger.info('[Server] GetSoldoutData Request');
  const response = await axios.get<string[]>('/menu-data/soldout');
  logger.info('[Server] GetSoldoutData Response', response.data);
  return response.data;
};

/** 서버 상태 체크. 성공 시 HttpStatus OK */
export const checkStatus = async (): Promise<void> => {
  logger.info('[Server] CheckStatus Request');
  await axios.get('/config/check');
  logger.info('[Server] CheckStatus Response');
};

export const callRobowide = async (tableCode: string): Promise<void> => {
  logger.info('[Server] CallRobowide Request');
  const response = await axios.post('/robot/robowide/call', { tableCode });
  logger.info('[Server] CallRobowide Response => ', response.data);
};

export const callKeenonRobot = async (tableCode: string): Promise<BridgeBaseResponse> => {
  try {
    logger.info('[Server] CallKeenonRobot Request');
    const response = await axios.post('/robot/keenon/call', { tableCode }, { skipInterceptorResponse: true });
    logger.info('[Server] CallKeenonRobot Response => ', response.data);
    return response.data;
  } catch (error: any) {
    const axiosError = error as AxiosError<BridgeBaseResponse>;

    if (axiosError.response?.data) {
      // 서버에서 응답값이 있는 경우
      return axiosError.response.data;
    }
    throw error;
  }
};

/**
 * 서버 로깅
 * @param level 로그 수준
 * @param argumentArray 로그 내용
 */
export const logging = async (level: LogLevel, argumentArray: any[]): Promise<void> => {
  const configStore = useConfigStore();

  if (!configStore.config.StoreCode) return;
  if (!configStore.config.IsServerLoggingUsed) return;

  await axios.post(`/UPCloud/TraceLog`, {
    STORE_CODE: configStore.config.StoreCode,
    SUB_STORE_CODE: configStore.config.SubStoreCode,
    POSID: configStore.config.PosCode,
    ProgramType: '1005',
    LogType: level === 'error' ? '1' : '0',
    LogMessage: argumentArray[0],
    TraceLog: argumentArray.toString(),
  });
};

/**
 * 광고 조회
 */
export const getAds = async (): Promise<ResponseAd[]> => {
  logger.info('[Server] GetAds Request');
  const configStore = useConfigStore();
  const response = await axios.get<ResponseAd[]>('/UPCloud/Advertisement', {
    params: {
      StoreID: configStore.config.StoreID,
      SubStoreCode: configStore.config.SubStoreCode,
      ADDevice: 'D',
      CurrentDateTime: new Date().toISOString(),
    },
  });
  logger.info('[Server] GetAds Response', response.data);
  return response.data;
};

/**
 * 광고 로그 전송
 * @param logs 광고 로그
 */
export const uploadAdLogs = async (logs: RequestAdLog[]): Promise<void> => {
  logger.info('[Server] UploadAdLogs Request');
  await axios.post('/UPCloud/AdvertisementLog', logs);
  logger.info('[Server] UploadAdLogs Response');
};

/**
 * 회원 조회
 * @param mobileNumber
 * @returns
 */
export const getCustomer = async (mobileNumber: string): Promise<OrderCloudCustomer | null> => {
  logger.info('[Server] GetCustomer Request', mobileNumber);
  try {
    const response = await axios.get<OrderCloudCustomer | null>('/customers', {
      params: { PhoneNumber: mobileNumber },
    });
    logger.info('[Server] GetCustomer Response', response.data);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.response && error.response.status === 404) {
      return null;
    } else {
      throw error;
    }
  }
};

/**
 * 회원 추가
 * @param mobileNumber
 * @returns 추가된 고객 정보
 */
export const addCustomer = async (mobileNumber: string): Promise<OrderCloudCustomer> => {
  logger.info('[Server] AddCustomer Request', mobileNumber);
  const response = await axios.post<OrderCloudCustomer>('/customers', {
    PhoneNumber: mobileNumber,
  });
  logger.info('[Server] AddCustomer Response', response.data);
  return response.data;
};

/**
 * 포인트 사용
 * @param requestPoint 포인트 사용 요청 데이터
 * @returns 포인트 사용 전표번호
 */
export const usePoints = async (requestPoint: RequestPoint): Promise<ResponsePoint> => {
  logger.info('[Server] UsePoints Request', requestPoint);
  const response = await axios.post<ResponsePoint>('/points/use', requestPoint);
  logger.info('[Server] UsePoints Response', response.data);
  return response.data;
};

/**
 * 포인트 사용 취소
 * @param requestPoint 포인트 사용 취소 요청 데이터
 * @returns 포인트 사용 전표번호
 */
export const useCancelPoints = async (requestPoint: RequestPoint): Promise<ResponsePoint> => {
  logger.info('[Server] UseCancelPoints Request', requestPoint);
  const response = await axios.post<ResponsePoint>('/points/use-cancel', requestPoint);
  logger.info('[Server] UseCancelPoints Response', response.data);
  return response.data;
};
